import React from 'react';
import { Box, Container } from '@mui/material';
import NavigationBar from '../../components/NavigationBar.jsx';
import { CONTENT_TOP_OFFSET, PAGE_GUTTER } from '../../theme/tokens';

/**
 * AppShell — nav + content frame for every screen.
 *
 * AppShell OWNS the navbar top offset (docs/UI_STANDARDS.md §2.1). Pages must NOT
 * add `mt`/`marginTop`/`pt` to clear the AppBar — that double-offset is the single
 * most common layout bug in this codebase (§1.1). If content sits too high, fix it
 * here, once, for every page.
 *
 * Props
 * - maxWidth: Container maxWidth ('xl' default; `false` = full-bleed, for boards)
 * - disablePadding: opt out of the frame entirely (page manages its own chrome)
 * - fullHeight: content fills the viewport below the nav without page scroll
 *   (for board/inbox layouts that scroll internally)
 * - rightActions: passed through to NavigationBar's app bar
 */
const AppShell = ({
    children,
    maxWidth = 'xl',
    disablePadding = false,
    fullHeight = false,
    rightActions = null,
}) => {
    const frameSx = disablePadding
        ? { pt: 0, pb: 0, px: 0 }
        : { pt: `${CONTENT_TOP_OFFSET}px`, pb: PAGE_GUTTER, px: PAGE_GUTTER };

    const content = disablePadding ? (
        children
    ) : (
        <Container
            maxWidth={maxWidth}
            sx={
                fullHeight
                    ? // Grow into the frame's remaining height rather than height:100%,
                      // which would resolve against the padded parent and overflow.
                      // minHeight:0 lets inner scroll areas actually shrink.
                      { display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }
                    : {}
            }
        >
            {children}
        </Container>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <NavigationBar rightActions={rightActions} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    bgcolor: 'background.default',
                    // fullHeight pages scroll internally, so the frame itself must not.
                    ...(fullHeight && { height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }),
                    ...frameSx,
                }}
            >
                {content}
            </Box>
        </Box>
    );
};

export default AppShell;
